import { useCallback } from "react";
import { Country } from "@lookiero/sty-psp-locale";
import { Segment } from "@lookiero/sty-psp-segment";
import { TrackingEventCategory, useEmitUserEvent } from "@lookiero/sty-psp-tracking";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { Product } from "../../../domain/entities/product/Product";
import { Experiments } from "../../../domain/repositories/ABTestClient";
import { financial } from "../shared/util/financial/financial";
import { experimentNameShortener } from "./experimentNameShortener";
import { CheckoutStartedTrackingEvent, Constants, PROJECT, TrackingEventName } from "./tracking";

interface TrackCheckoutStartedFunctionArgs {
  readonly products: Product[];
  readonly experiments: Experiments | undefined;
}

interface TrackCheckoutStartedFunction {
  (args: TrackCheckoutStartedFunctionArgs): void;
}

interface UseTrackCheckoutStartedFunctionArgs {
  readonly country: Country;
  readonly segment: Segment;
  readonly userId: string;
  readonly tradename: Tradename;
}

interface UseTrackCheckoutStartedFunction {
  (agrs: UseTrackCheckoutStartedFunctionArgs): TrackCheckoutStartedFunction;
}

const useTrackCheckoutStarted: UseTrackCheckoutStartedFunction = ({ country, segment, tradename, userId }) => {
  const emitUserEvent = useEmitUserEvent<CheckoutStartedTrackingEvent>();

  const trackCheckoutStarted: TrackCheckoutStartedFunction = useCallback(
    ({ products, experiments }) => {
      emitUserEvent({
        event: TrackingEventName.CHECKOUT_STARTED,
        eventCategory: TrackingEventCategory.LOOK_IN,
        store: country,
        section: PROJECT,
        segment,
        tradename,
        userId,
        userUuid: userId,
        ecommerce: {
          checkout: {
            actionField: {
              step: 1,
            },
            products: products.map((item) => ({
              name: item.title,
              id: item.variantId,
              price: financial(item.price.amount),
              brand: item.brand,
              category: Constants.NOT_SET,
              variant: item.size,
              list: Constants.COMPLETE_YOUR_LOOK_LIST,
              quantity: Constants.QUANTITY,
            })),
          },
        },
        items: products.map((item) => ({
          item_id: item.variantId,
          item_list_name: Constants.COMPLETE_YOUR_LOOK_LIST,
        })),
        experiments: experiments ? JSON.stringify(experimentNameShortener(experiments)) : undefined,
      });
    },
    [country, emitUserEvent, segment, tradename, userId],
  );

  return trackCheckoutStarted;
};

export { useTrackCheckoutStarted };
